import React, { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../supabase/supabaseClient'
import { convertOdds } from '../utils/oddsConverter'
import { BOOKMAKER_LOGOS, BOOKMAKERS_BY_REGION as BOOKMAKERS } from '../config/bookmakers'
import { ALL_SPORTS as SPORTS } from '../config/sports'
import { saveUserBet, getRecentBetsOfType, saveUserFilter, getUserFilters, deleteUserFilter, setDefaultFilter, unsetDefaultFilter, getDefaultFilter } from '../services/userDataService'
import FilterPanel from './FilterPanel'
import HiddenBetsModal from './HiddenBetsModal'
import TrackBetModal from './TrackBetModal'
import ArbCard, { formatSelection } from './ArbCard'
import ArbTour from './arbTour'
import PreviewWall from '../components/PreviewWall'
import '../styles/app.css'
import '../styles/app-mobile.css'
import './ArbNew.css'
import useVisiblePolling from '../hooks/useVisiblePolling'

const getHoursUntil = (ct) => Math.max(0, (new Date(ct + 'Z') - new Date()) / 3600000)

export default function ArbNewMobile({ isVisible = true, isPreview: isPreviewProp = false }) {
  const { user, oddsFormat, hasAccess, hadTrial, loading: authLoading } = useAuth()
  const isPreview = isPreviewProp || authLoading || !hasAccess('arb')

  // ─── Data ────────────────────────────────────────────────────────────────
  const [opportunities, setOpportunities]           = useState([])
  const [loading, setLoading]                       = useState(true)
  const [error, setError]                           = useState(null)
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false)

  // ─── Stake settings ──────────────────────────────────────────────────────
  const [totalStake, setTotalStake]   = useState(100)
  const [roundStakes, setRoundStakes] = useState('1')

  // ─── Filters ─────────────────────────────────────────────────────────────
  const [filters, setFilters] = useState({
    sports:             new Set(),
    bookmakers:         new Set(),
    requiredBookmakers: new Set(),
    odds:               [1, 10],
    profit:             [0, 50],
    hoursUntil:         [0, 72],
  })
  const [savedFilters, setSavedFilters]           = useState([])
  const [filterName, setFilterName]               = useState('')
  const [activeFilterId, setActiveFilterId]       = useState(null)
  const [defaultFilterLoaded, setDefaultFilterLoaded] = useState(false)
  const [showFilterPanel, setShowFilterPanel]     = useState(false)

  // ─── Sort + pagination ───────────────────────────────────────────────────
  const [sortBy, setSortBy]           = useState('profit_percentage')
  const [sortDir, setSortDir]         = useState('desc')
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 25

  // ─── Card state ──────────────────────────────────────────────────────────
  const [expandedCard, setExpandedCard] = useState(null)
  const [editableData, setEditableData] = useState({})

  // ─── Track ───────────────────────────────────────────────────────────────
  const [trackedBets, setTrackedBets]                 = useState(new Set())
  const [showTrackModal, setShowTrackModal]           = useState(false)
  const [trackingOpportunity, setTrackingOpportunity] = useState(null)
  const [trackBetForm, setTrackBetForm] = useState({
    sport: '', match: '', market_type: '',
    bookmaker: '', bookmaker2: '',
    selection1: '', selection2: '',
    stake: '', profit: '',
    date: new Date().toISOString().split('T')[0],
  })

  // ─── Hidden ──────────────────────────────────────────────────────────────
  const [hiddenBets, setHiddenBets]         = useState({})
  const [showHiddenModal, setShowHiddenModal] = useState(false)

  // ─── Live dot tick ───────────────────────────────────────────────────────
  // eslint-disable-next-line no-unused-vars
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 10000)
    return () => clearInterval(id)
  }, [])

  // ─── Fetch ────────────────────────────────────────────────────────────────
  const fetchOpportunities = async (silent = false) => {
    try {
      if (!silent) setLoading(true)
      const { data, error } = await supabase.functions.invoke('arb-proxy')
      if (error) throw error
      setOpportunities(data.opportunities || [])
      setError(null)
    } catch (err) {
      setError(err.message)
    } finally {
      if (!silent) setLoading(false)
    }
  }

  useEffect(() => { fetchOpportunities() }, [])

  useVisiblePolling(() => fetchOpportunities(true), 30000, autoRefreshEnabled && isVisible)

  // ─── Hidden bets bootstrap ───────────────────────────────────────────────
  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem('hidden-arb-bets') || '{}')
    const now = new Date()
    const cleaned = {}
    Object.entries(stored).forEach(([id, data]) => {
      const gameTime = new Date(data.commence_time + 'Z')
      if (now < new Date(gameTime.getTime() + 3600000)) cleaned[id] = data
    })
    localStorage.setItem('hidden-arb-bets', JSON.stringify(cleaned))
    setHiddenBets(cleaned)
  }, [])

  // ─── Filter persistence ──────────────────────────────────────────────────
  useEffect(() => {
    if (!user?.id) {
      setFilters(prev => ({ ...prev, sports: new Set(SPORTS), bookmakers: new Set(BOOKMAKERS.AU) }))
      return
    }
    getUserFilters(user.id, 'arb').then(({ data }) => {
      if (data) setSavedFilters(data.map(f => ({ id: f.id, name: f.filter_name, isDefault: f.is_default || false, ...f.filter_data })))
    })
  }, [user?.id])

  useEffect(() => {
    if (!user?.id || defaultFilterLoaded) return
    getDefaultFilter(user.id, 'arb').then(({ data }) => {
      if (data?.filter_data) applyFilterData(data.filter_data)
      else setFilters(prev => ({ ...prev, sports: new Set(SPORTS), bookmakers: new Set(BOOKMAKERS.AU) }))
      setDefaultFilterLoaded(true)
    })
  }, [user?.id, defaultFilterLoaded])

  const applyFilterData = (fd) => {
    // Prefer the saved excludedSports blacklist (newer filters) so leagues added after the
    // filter was saved stay visible (selected = all minus excluded). Fall back to the legacy
    // sports whitelist for old filters.
    const selectedSports = Array.isArray(fd.filters.excludedSports)
      ? SPORTS.filter(s => !fd.filters.excludedSports.includes(s))
      : (fd.filters.sports || SPORTS)
    setFilters({
      sports:             new Set(selectedSports),
      bookmakers:         new Set(fd.filters.bookmakers || BOOKMAKERS.AU),
      requiredBookmakers: new Set(fd.filters.requiredBookmakers || []),
      odds:       fd.filters.odds       || [1, 10],
      profit:     fd.filters.profit     || [0, 50],
      hoursUntil: fd.filters.hoursUntil || [0, 72],
    })
    if (fd.stakeSettings) {
      setTotalStake(fd.stakeSettings.totalStake ?? 100)
      setRoundStakes(fd.stakeSettings.roundStakes ?? '1')
    }
  }

  const handleSaveFilter = async (name) => {
    if (!name.trim() || !user?.id) return
    const fd = {
      filters: {
        sports:             Array.from(filters.sports),                       // legacy whitelist (back-compat)
        excludedSports:     SPORTS.filter(s => !filters.sports.has(s)),       // blacklist = source of truth on load
        bookmakers:         Array.from(filters.bookmakers),
        requiredBookmakers: Array.from(filters.requiredBookmakers),
        odds:       filters.odds,
        profit:     filters.profit,
        hoursUntil: filters.hoursUntil,
      },
      stakeSettings: { totalStake, roundStakes },
      createdAt: new Date().toISOString(),
    }
    const { data, error } = await saveUserFilter(user.id, 'arb', name.trim(), fd)
    if (error) { alert('Error saving filter: ' + error); return }
    if (data) setSavedFilters(prev => [...prev, { id: data.id, name: data.filter_name, isDefault: false, ...data.filter_data }])
    setFilterName('')
  }

  const handleLoadFilter    = (saved) => { applyFilterData(saved); setActiveFilterId(saved.id) }
  const handleDeleteFilter  = async (id) => {
    if (!user?.id) return
    const { error } = await deleteUserFilter(id)
    if (error) { alert('Error deleting filter: ' + error); return }
    setSavedFilters(prev => prev.filter(f => f.id !== id))
    if (activeFilterId === id) setActiveFilterId(null)
  }
  const handleToggleDefault = async (id) => {
    if (!user?.id) return
    const current = savedFilters.find(f => f.id === id)
    if (current?.isDefault) await unsetDefaultFilter(user.id, 'arb', id)
    else await setDefaultFilter(user.id, 'arb', id)
    setSavedFilters(prev => prev.map(f => ({ ...f, isDefault: f.id === id ? !current?.isDefault : false })))
  }
  const resetFilters = () => {
    setFilters({ sports: new Set(SPORTS), bookmakers: new Set(BOOKMAKERS.AU), requiredBookmakers: new Set(), odds: [1, 10], profit: [1, 50], hoursUntil: [1, 24] })
    setTotalStake(100)
    setRoundStakes('1')
  }

  // ─── Arb calc ────────────────────────────────────────────────────────────
  const calculateArbStakes = (outcomes, customOdds = null, customStakeTotal = null) => {
    if (!outcomes || outcomes.length !== 2) return { stakes: [], profit: 0, totalStaked: 0, individualProfits: [] }
    const odds1 = customOdds?.[0] ?? outcomes[0].odds
    const odds2 = customOdds?.[1] ?? outcomes[1].odds
    const stakeToUse = customStakeTotal ?? totalStake
    const totalImplied = (1 / odds1) + (1 / odds2)
    const raw1 = stakeToUse / (odds1 * totalImplied)
    const raw2 = stakeToUse / (odds2 * totalImplied)
    const round = (v) => {
      if (roundStakes === 'none') return Math.round(v * 100) / 100
      if (roundStakes === '1')    return Math.round(v)
      if (roundStakes === '5')    return Math.round(v / 5) * 5
      return v
    }
    const s1 = round(raw1), s2 = round(raw2)
    const p1 = Math.round(((s1 * odds1) - (s1 + s2)) * 100) / 100
    const p2 = Math.round(((s2 * odds2) - (s1 + s2)) * 100) / 100
    return { stakes: [s1, s2], profit: Math.round(((p1 + p2) / 2) * 100) / 100, totalStaked: s1 + s2, individualProfits: [p1, p2] }
  }

  const getEditableOdds    = (id, idx, def) => editableData[id]?.odds?.[idx] !== undefined ? editableData[id].odds[idx] : def
  const getEditableOddsRaw = (id, idx, def) => editableData[id]?.oddsRaw?.[idx] !== undefined ? editableData[id].oddsRaw[idx] : String(def)
  const getEditableStake   = (id, def)      => editableData[id]?.totalStake !== undefined ? editableData[id].totalStake : def

  const updateOdds = (id, idx, val) => setEditableData(prev => {
    const d = { ...(prev[id] || {}) }
    d.odds = [...(d.odds || [])]
    d.oddsRaw = [...(d.oddsRaw || [])]
    d.oddsRaw[idx] = val
    const num = parseFloat(val)
    d.odds[idx] = isNaN(num) ? d.odds[idx] : num
    return { ...prev, [id]: d }
  })

  const updateStake = (id, val) => setEditableData(prev => {
    const num = parseFloat(val)
    return { ...prev, [id]: { ...(prev[id] || {}), totalStakeRaw: val, totalStake: isNaN(num) ? (prev[id]?.totalStake ?? totalStake) : num } }
  })

  const getEditableStakeRaw = (id, def) => editableData[id]?.totalStakeRaw !== undefined ? editableData[id].totalStakeRaw : String(def)

  // ─── Hidden ──────────────────────────────────────────────────────────────
  const generateHideId = (opp) => {
    const s1 = opp.outcomes?.[0]?.selection || '', s2 = opp.outcomes?.[1]?.selection || ''
    const b1 = opp.outcomes?.[0]?.bookmaker || '', b2 = opp.outcomes?.[1]?.bookmaker || ''
    return `${opp.sport}_${opp.match}_${opp.market_type}_${s1}_${s2}_${b1}_${b2}_${opp.commence_time}`.replace(/[^a-zA-Z0-9_]/g, '_')
  }

  const hideOpportunity = (opp) => {
    const id = generateHideId(opp)
    const updated = { ...hiddenBets, [id]: { match: opp.match, selection1: opp.outcomes?.[0]?.selection, selection2: opp.outcomes?.[1]?.selection, bookmaker1: opp.outcomes?.[0]?.bookmaker, bookmaker2: opp.outcomes?.[1]?.bookmaker, commence_time: opp.commence_time, sport: opp.sport, market_type: opp.market_type } }
    localStorage.setItem('hidden-arb-bets', JSON.stringify(updated))
    setHiddenBets(updated)
  }

  const unhideOpportunity = (betId) => {
    const updated = { ...hiddenBets }
    delete updated[betId]
    localStorage.setItem('hidden-arb-bets', JSON.stringify(updated))
    setHiddenBets(updated)
  }

  const clearAllHidden = () => { localStorage.removeItem('hidden-arb-bets'); setHiddenBets({}) }

  // ─── Track ───────────────────────────────────────────────────────────────
  const generateBetId = (opp) => {
    const b1 = opp.outcomes?.[0]?.bookmaker || '', b2 = opp.outcomes?.[1]?.bookmaker || ''
    const s1 = opp.outcomes?.[0]?.selection || '', s2 = opp.outcomes?.[1]?.selection || ''
    return `${opp.sport}_${opp.match}_${opp.market_type || ''}_${s1}_${s2}_${b1}_${b2}`.replace(/[^a-zA-Z0-9_]/g, '_')
  }

  const isTracked = (opp) => trackedBets.has(generateBetId(opp))

  // Once per user, not per opportunities refresh: a save adds its own id to
  // the set, so refetching on every poll only re-read the same bets. Reads
  // the last week of this type rather than the 15 most recent bets of any
  // type, which let older tracked opportunities lose their tick.
  useEffect(() => {
    if (!user?.id) return
    getRecentBetsOfType(user.id, 'ARB').then(({ data }) => {
      if (!data) return
      const ids = data.map(b => {
        const d = b.bet_data
        return `${d.sport}_${d.match}_${d.market_type || ''}_${d.selection1 || ''}_${d.selection2 || ''}_${d.bookmaker || ''}_${d.bookmaker2 || ''}`.replace(/[^a-zA-Z0-9_]/g, '_')
      })
      setTrackedBets(new Set(ids))
    })
  }, [user?.id])

  const openTrackModal = (opp) => {
    setTrackingOpportunity(opp)
    const arbStake = calculateArbStakes(opp.outcomes)
    setTrackBetForm({
      sport: opp.sport, match: opp.match, market_type: opp.market_type,
      bookmaker: opp.outcomes?.[0]?.bookmaker || '',
      bookmaker2: opp.outcomes?.[1]?.bookmaker || '',
      selection1: opp.outcomes?.[0]?.selection || '',
      selection2: opp.outcomes?.[1]?.selection || '',
      stake: totalStake,
      profit: arbStake.profit || '',
      date: new Date(opp.commence_time + 'Z').toLocaleDateString('en-CA'),
    })
    setShowTrackModal(true)
  }

  const closeTrackModal = () => {
    setShowTrackModal(false)
    setTrackingOpportunity(null)
    setTrackBetForm({ sport: '', match: '', market_type: '', bookmaker: '', bookmaker2: '', selection1: '', selection2: '', stake: '', profit: '', date: new Date().toISOString().split('T')[0] })
  }

  const saveTrack = async (andHide = false) => {
    if (!user?.id) { alert('Please log in to track bets'); return }
    const newBet = {
      type: 'ARB', sport: trackBetForm.sport, match: trackBetForm.match,
      market_type: trackBetForm.market_type, bookmaker: trackBetForm.bookmaker,
      bookmaker2: trackBetForm.bookmaker2, selection1: trackBetForm.selection1,
      selection2: trackBetForm.selection2, stake: parseFloat(trackBetForm.stake),
      status: trackBetForm.profit ? (parseFloat(trackBetForm.profit) > 0 ? 'won' : 'lost') : 'pending',
      actual_profit: trackBetForm.profit ? parseFloat(trackBetForm.profit) : null,
      // Placed is the moment it was tracked; the game's own time is
      // commence_time, which the tracker shows as the bet's date. A profit
      // entered here settles it at that game time.
      commence_time: trackingOpportunity?.commence_time ?? null,
      placed_at: new Date().toISOString(),
      settled_at: trackBetForm.profit
        ? (trackingOpportunity?.commence_time ? new Date(trackingOpportunity.commence_time + 'Z') : new Date()).toISOString()
        : null,
    }
    const { error } = await saveUserBet(user.id, newBet)
    if (error) { alert('Error saving bet. Please try again.'); return }
    setTrackedBets(prev => new Set([...prev, generateBetId(trackingOpportunity)]))
    if (andHide) hideOpportunity(trackingOpportunity)
    closeTrackModal()
  }

  // ─── Filter + sort ───────────────────────────────────────────────────────
  const filteredOpportunities = useMemo(() => {
    const filtered = opportunities.filter(opp => {
      if (filters.sports.size > 0 && !filters.sports.has(opp.sport)) return false
      if (opp.outcomes && !opp.outcomes.every(o => filters.bookmakers.has(o.bookmaker))) return false
      if (filters.requiredBookmakers?.size > 0 && opp.outcomes &&
          !opp.outcomes.some(o => filters.requiredBookmakers.has(o.bookmaker))) return false
      if (opp.outcomes && opp.outcomes.some(o => o.odds < filters.odds[0] || o.odds > filters.odds[1])) return false
      if (opp.profit_percentage < filters.profit[0] || opp.profit_percentage > filters.profit[1]) return false
      const hrs = getHoursUntil(opp.commence_time)
      if (hrs < filters.hoursUntil[0] || hrs > filters.hoursUntil[1]) return false
      return true
    })
    return filtered.sort((a, b) => {
      let av, bv
      if (sortBy === 'profit_percentage')       { av = a.profit_percentage; bv = b.profit_percentage }
      else if (sortBy === 'odds')               { av = a.outcomes?.reduce((s, o) => s + o.odds, 0) / (a.outcomes?.length || 1); bv = b.outcomes?.reduce((s, o) => s + o.odds, 0) / (b.outcomes?.length || 1) }
      else if (sortBy === 'start_time')         { av = new Date(a.commence_time).getTime(); bv = new Date(b.commence_time).getTime() }
      else if (sortBy === 'sport')              { return sortDir === 'desc' ? b.sport.localeCompare(a.sport) : a.sport.localeCompare(b.sport) }
      else                                      { av = 0; bv = 0 }
      return sortDir === 'desc' ? bv - av : av - bv
    })
  }, [opportunities, filters, sortBy, sortDir])

  const visibleOpportunities = useMemo(
    () => filteredOpportunities.filter(opp => !hiddenBets[generateHideId(opp)]),
    [filteredOpportunities, hiddenBets]
  )

  const totalPages = Math.max(1, Math.ceil(visibleOpportunities.length / itemsPerPage))
  useEffect(() => { if (currentPage > totalPages) setCurrentPage(1) }, [totalPages, currentPage])
  useEffect(() => { setCurrentPage(1) }, [filters, sortBy, sortDir])

  const pagedOpps = isPreview
    ? visibleOpportunities.slice(0, 3)
    : visibleOpportunities.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

  const hiddenCount = Object.keys(hiddenBets).length

  if (!isVisible) return null

  return (
    <div className="bcm-container">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="bcm-header">
        <div className="bcm-action-row">

          <div className="bcm-action-left">
            <button className="bc-btn" onClick={() => setShowFilterPanel(true)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
              </svg>
              Filters
            </button>

            {hiddenCount > 0 && (
              <button className="bc-btn" onClick={() => setShowHiddenModal(true)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
                {hiddenCount}
              </button>
            )}
          </div>

          <div className="bcm-action-right">
            <button
              className={`bc-btn bc-btn-toggle${autoRefreshEnabled ? ' toggle-active' : ''}`}
              onClick={() => setAutoRefreshEnabled(v => !v)}
            >
              Auto
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
            </button>
            <button className="bc-btn bc-icon-btn" onClick={() => fetchOpportunities()} disabled={loading}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={loading ? 'bc-spin' : ''}>
                <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
            </button>
          </div>

        </div>
      </div>

      {error && <div className="bc-error" style={{ margin: '12px' }}>Error: {error}</div>}

      {loading && <div className="bc-loading"><div className="bc-spinner" /></div>}

      {/* ── Cards ──────────────────────────────────────────────────────── */}
      {!loading && (
        <div className="bcm-cards">
          {pagedOpps.map((opp, idx) => {
            const rowId      = opp._id
            const isExpanded = expandedCard === rowId
            const blurCard   = isPreview && idx > 0
            const blurStyle  = blurCard ? { filter: 'blur(5px)', userSelect: 'none', pointerEvents: 'none' } : {}

            const customOdds  = [getEditableOdds(rowId, 0, opp.outcomes?.[0]?.odds ?? 0), getEditableOdds(rowId, 1, opp.outcomes?.[1]?.odds ?? 0)]
            const customStake = getEditableStake(rowId, totalStake)
            const arbCalc     = calculateArbStakes(opp.outcomes, customOdds, customStake)

            return (
              <React.Fragment key={rowId}>
                <ArbCard
                  opp={opp}
                  oddsText={customOdds.map(o => convertOdds(o, oddsFormat))}
                  stakes={arbCalc.stakes}
                  style={blurStyle}
                  blurStats={isPreview && idx === 0}
                  isExpanded={isExpanded}
                  tracked={isTracked(opp)}
                  showButtons={!isPreview}
                  onOpenBook={(outcome) => !isPreview && outcome.game_url && window.open(outcome.game_url, '_blank')}
                  onExpand={() => setExpandedCard(isExpanded ? null : rowId)}
                  onTrack={() => !isTracked(opp) && openTrackModal(opp)}
                  onHide={() => hideOpportunity(opp)}
                >
                  {/* ── Expanded calculator ───────────────────────────── */}
                  {isExpanded && (
                    <div className="arb-mob-expanded">

                      {/* Stake bar */}
                      <div className="arb-mob-stake-bar">
                        <span className="arb-mob-stake-label">Total Stake</span>
                        <div className="arb-exp-topbar-input-wrap">
                          <span className="arb-exp-topbar-prefix">$</span>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={getEditableStakeRaw(rowId, totalStake)}
                            onChange={e => updateStake(rowId, e.target.value)}
                            className="arb-exp-stake-input"
                          />
                        </div>
                      </div>

                      {/* Outcome cards */}
                      {opp.outcomes?.map((outcome, oi) => {
                        const logo = BOOKMAKER_LOGOS[outcome.bookmaker]
                        const profit = arbCalc.individualProfits[oi]
                        return (
                          <div key={oi} className="arb-mob-outcome-card">
                            <div className="arb-mob-card-header">
                              {logo
                                ? <img src={logo} alt={outcome.bookmaker} className="arb-outcome-card-logo" />
                                : <div className="arb-outcome-card-logo-placeholder">{outcome.bookmaker.slice(0, 3).toUpperCase()}</div>
                              }
                              <div>
                                <div className="arb-outcome-card-name">{outcome.bookmaker}</div>
                                <div className="arb-outcome-card-sel">{formatSelection(outcome.selection, opp.market_type, opp.player_name)}</div>
                              </div>
                            </div>
                            <div className="arb-mob-card-fields">
                              <div className="arb-mob-field">
                                <span className="arb-outcome-field-label">Odds</span>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={getEditableOddsRaw(rowId, oi, opp.outcomes?.[oi]?.odds ?? 0)}
                                  onChange={e => updateOdds(rowId, oi, e.target.value)}
                                  className="arb-outcome-odds-input"
                                />
                              </div>
                              <div className="arb-mob-field">
                                <span className="arb-outcome-field-label">Stake</span>
                                <span className="arb-outcome-field-val">${arbCalc.stakes[oi] ?? '—'}</span>
                              </div>
                              <div className="arb-mob-field">
                                <span className="arb-outcome-field-label">If wins</span>
                                <span className={typeof profit === 'number' && profit >= 0 ? 'arb-outcome-field-profit-pos' : 'arb-outcome-field-profit-neg'}>
                                  {typeof profit === 'number' ? `$${profit}` : '—'}
                                </span>
                              </div>
                            </div>
                            <button className="arb-open-btn" onClick={() => outcome.game_url && window.open(outcome.game_url, '_blank')} disabled={!outcome.game_url}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                                <polyline points="15 3 21 3 21 9"/>
                                <line x1="10" y1="14" x2="21" y2="3"/>
                              </svg>
                              Open {outcome.bookmaker}
                            </button>
                          </div>
                        )
                      })}
                    </div>
                  )}

                </ArbCard>

              {/* Preview wall — right below the first (teaser) card */}
              {isPreview && idx === 0 && (
                <PreviewWall
              title={visibleOpportunities.length > 0
                ? `${visibleOpportunities.length} arbitrage opportunities right now`
                : 'Arbitrage opportunities right now'}
              features={['Real time odds from all major bookmakers', 'Automatic stake calculation', 'Direct links to place your bets', 'Much more...']}
              planName="Sports Premium"
              accent="#60a5fa"
              hadTrial={hadTrial}
            />
              )}
              </React.Fragment>
            )
          })}

          {/* Preview wall fallback — still shows when there are zero opportunities */}
          {isPreview && pagedOpps.length === 0 && (
            <PreviewWall
              title={visibleOpportunities.length > 0
                ? `${visibleOpportunities.length} arbitrage opportunities right now`
                : 'Arbitrage opportunities right now'}
              features={['Real time odds from all major bookmakers', 'Automatic stake calculation', 'Direct links to place your bets', 'Much more...']}
              planName="Sports Premium"
              accent="#60a5fa"
              hadTrial={hadTrial}
            />
          )}

          {!isPreview && visibleOpportunities.length === 0 && (
            <div className="bc-empty">No arbitrage opportunities match your filters.</div>
          )}
        </div>
      )}

      {/* ── Pagination ─────────────────────────────────────────────────── */}
      {!isPreview && !loading && totalPages > 1 && (
        <div className="bcm-pagination">
          <button className="bc-page-arrow" onClick={() => { setCurrentPage(1); window.scrollTo({ top: 0 }) }} disabled={currentPage === 1}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="11 18 5 12 11 6"/><polyline points="19 18 13 12 19 6"/></svg>
          </button>
          <button className="bc-page-arrow" onClick={() => { setCurrentPage(p => Math.max(1, p - 1)); window.scrollTo({ top: 0 }) }} disabled={currentPage === 1}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <span className="bc-page-info">{currentPage} / {totalPages}</span>
          <button className="bc-page-arrow" onClick={() => { setCurrentPage(p => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0 }) }} disabled={currentPage === totalPages}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
          <button className="bc-page-arrow" onClick={() => { setCurrentPage(totalPages); window.scrollTo({ top: 0 }) }} disabled={currentPage === totalPages}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="5 18 11 12 5 6"/><polyline points="13 18 19 12 13 6"/></svg>
          </button>
        </div>
      )}

      {/* ── Modals ─────────────────────────────────────────────────────── */}
      <FilterPanel
        isOpen={showFilterPanel}
        onClose={() => setShowFilterPanel(false)}
        filters={filters}
        setFilters={setFilters}
        totalStake={totalStake}
        setTotalStake={setTotalStake}
        roundStakes={roundStakes}
        setRoundStakes={setRoundStakes}
        savedFilters={savedFilters}
        activeFilterId={activeFilterId}
        filterName={filterName}
        setFilterName={setFilterName}
        onSaveFilter={handleSaveFilter}
        onLoadFilter={handleLoadFilter}
        onDeleteFilter={handleDeleteFilter}
        onToggleDefault={handleToggleDefault}
        onReset={resetFilters}
        sortBy={sortBy}
        setSortBy={setSortBy}
        sortDir={sortDir}
        setSortDir={setSortDir}
      />

      <HiddenBetsModal
        isOpen={showHiddenModal}
        hiddenBets={hiddenBets}
        onUnhide={unhideOpportunity}
        onUnhideAll={clearAllHidden}
        onClose={() => setShowHiddenModal(false)}
      />

      <TrackBetModal
        isOpen={showTrackModal}
        opp={trackingOpportunity}
        form={trackBetForm}
        onFormChange={setTrackBetForm}
        onConfirm={() => saveTrack(false)}
        onConfirmAndHide={() => saveTrack(true)}
        onClose={closeTrackModal}
      />

      <ArbTour isVisible={isVisible} isMobile />

    </div>
  )
}
